import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients, contacts, opportunities, calls, tasks } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Building, Mail, Phone, MoreVertical, Trash2, Edit, 
  CalendarClock, Target, UserPlus, FileText, PhoneOutgoing, PhoneIncoming, Briefcase, CheckSquare
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

const CLIENT_TYPES = {
  consumidor: { label: 'Consumidor Final', description: 'Factura de Consumo (NCF 02)' },
  credito_fiscal: { label: 'Credito Fiscal', description: 'Deducible ISR/ITBIS (NCF 01)' },
  gubernamental: { label: 'Gubernamental', description: 'Entidades del Estado (NCF 15)' },
  zona_franca: { label: 'Zona Franca', description: 'Regimen Especial (NCF 14)' },
};

const OPPORTUNITY_STATUS = {
  new: { label: 'Nuevo', color: 'bg-slate-500' },
  qualification: { label: 'Calificacion', color: 'bg-blue-500' },
  proposal: { label: 'Propuesta', color: 'bg-violet-500' },
  negotiation: { label: 'Negociacion', color: 'bg-amber-500' },
  won: { label: 'Ganada', color: 'bg-emerald-500' },
  lost: { label: 'Perdida', color: 'bg-rose-500' },
};

const VERTICALS = {
  logistics: { label: 'Servicios Logisticos', color: 'text-blue-600' },
  consulting: { label: 'Consultoria', color: 'text-violet-600' },
  technology: { label: 'Tecnologia', color: 'text-emerald-600' },
};

export default function Empresas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientsList, setClientsList] = useState([]);
  const [contactsList, setContactsList] = useState([]);
  const [opportunitiesList, setOpportunitiesList] = useState([]);
  const [callsList, setCallsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editClientDialogOpen, setEditClientDialogOpen] = useState(false);
  const [detailClientDialogOpen, setDetailClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState({ type: null, id: null, name: null });

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', address: '',
    rnc: '', client_type: 'credito_fiscal', department: '', notes: '',
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [cr, ctr, opr, clr] = await Promise.all([
        clients.getAll(), contacts.getAll(), opportunities.getAll(), calls.getAll(),
      ]);
      setClientsList(cr.data);
      setContactsList(ctr.data);
      setOpportunitiesList(opr.data);
      setCallsList(clr.data);
    } catch (err) {
      toast.error('Error al cargar datos');
    } finally { setLoading(false); }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (formData.client_type !== 'consumidor' && !formData.rnc) {
      toast.error('RNC es requerido para este tipo de empresa');
      return;
    }
    const submitData = { ...formData };
    if (formData.company && !formData.name) submitData.name = formData.company;
    try {
      await clients.create(submitData);
      toast.success('Empresa agregada');
      setClientDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', company: '', address: '', rnc: '', client_type: 'credito_fiscal', department: '', notes: '' });
      loadData();
    } catch (err) { toast.error('Error al crear empresa'); }
  };

  const handleOpenEditClient = (client) => {
    setSelectedClient(client);
    setEditFormData({
      name: client.name || '', company: client.company || '', email: client.email || '',
      phone: client.phone || '', address: client.address || '', rnc: client.rnc || '',
      client_type: client.client_type || 'consumidor', department: client.department || '', notes: client.notes || '',
    });
    setEditClientDialogOpen(true);
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    try {
      await clients.update(selectedClient.client_id, editFormData);
      toast.success('Empresa actualizada');
      setEditClientDialogOpen(false);
      loadData();
    } catch (err) { toast.error('Error al actualizar empresa'); }
  };

  const handleOpenClientDetail = (client) => {
    setSelectedClient(client);
    setDetailClientDialogOpen(true);
  };

  const handleDeleteClient = async (clientId) => {
    try { await clients.delete(clientId); toast.success('Empresa eliminada'); loadData(); }
    catch (err) { toast.error('Error al eliminar empresa'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="empresas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Building className="h-8 w-8 text-primary" />
            Empresas
          </h1>
          <p className="text-muted-foreground">Gestion de empresas y clientes</p>
        </div>
        <Button onClick={() => setClientDialogOpen(true)} data-testid="add-empresa-btn">
          <Plus className="h-4 w-4 mr-2" />Agregar Empresa
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Empresas</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{clientsList.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Contactos</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{contactsList.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Facturado</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-emerald-500">{formatCurrency(clientsList.reduce((s,c) => s + (c.total_invoiced || 0), 0))}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Por Cobrar</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-amber-500">{formatCurrency(clientsList.reduce((s,c) => s + (c.total_invoiced || 0) - (c.total_paid || 0), 0))}</p></CardContent></Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {clientsList.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No hay empresas registradas</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs">Empresa</TableHead>
                  <TableHead className="uppercase text-xs">Sector</TableHead>
                  <TableHead className="uppercase text-xs">Contactos</TableHead>
                  <TableHead className="uppercase text-xs">Ultimo Contacto</TableHead>
                  <TableHead className="uppercase text-xs text-right">Facturado</TableHead>
                  <TableHead className="uppercase text-xs text-right">Saldo</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientsList.map((client) => (
                  <TableRow key={client.client_id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleOpenClientDetail(client)}>
                    <TableCell>
                      <div className="font-medium flex items-center gap-2"><Building className="h-4 w-4 text-muted-foreground" />{client.company || client.name}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground ml-6">
                        {client.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>}
                        {client.rnc && <span className="flex items-center gap-1">RNC: {client.rnc}</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.department ? <div className="text-sm">{client.department}</div> : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{client.contacts_count || 0} contactos</Badge></TableCell>
                    <TableCell>
                      {client.last_contact_date ? (
                        <div className="flex items-center gap-1 text-sm"><CalendarClock className="h-3 w-3 text-muted-foreground" />{formatDate(client.last_contact_date)}</div>
                      ) : <span className="text-xs text-muted-foreground">Sin contacto</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(client.total_invoiced)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-500">{formatCurrency(client.total_invoiced - client.total_paid)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" onClick={(e) => e.stopPropagation()}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenEditClient(client); }}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteItem({ type: 'client', id: client.client_id, name: client.name }); setDeleteDialogOpen(true); }}><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Building className="h-5 w-5" />Nueva Empresa</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nombre de la Empresa *</Label><Input placeholder="Ej: Transtar SRL" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value, name: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Contacto Principal</Label><Input placeholder="Nombre del contacto" value={formData.name !== formData.company ? formData.name : ''} onChange={(e) => setFormData({ ...formData, name: e.target.value || formData.company })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo Fiscal (NCF) *</Label>
                <Select value={formData.client_type} onValueChange={(v) => setFormData({ ...formData, client_type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CLIENT_TYPES).map(([k, { label }]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>RNC {formData.client_type !== 'consumidor' && '*'}</Label><Input placeholder="000-00000-0" value={formData.rnc} onChange={(e) => setFormData({ ...formData, rnc: e.target.value })} required={formData.client_type !== 'consumidor'} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email Corporativo</Label><Input type="email" placeholder="info@empresa.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefono</Label><Input placeholder="809-000-0000" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Sector/Industria</Label><Input placeholder="Ej: Logistica, Manufactura" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} /></div>
              <div className="space-y-2"><Label>Direccion</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Notas</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>Cancelar</Button><Button type="submit">Agregar Empresa</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editClientDialogOpen} onOpenChange={setEditClientDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Edit className="h-5 w-5" />Editar Empresa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nombre de la Empresa *</Label><Input value={editFormData.company || ''} onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Contacto Principal</Label><Input value={editFormData.name !== editFormData.company ? editFormData.name : ''} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value || editFormData.company })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo Fiscal</Label>
                <Select value={editFormData.client_type || 'consumidor'} onValueChange={(v) => setEditFormData({ ...editFormData, client_type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CLIENT_TYPES).map(([k, { label }]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>RNC</Label><Input value={editFormData.rnc || ''} onChange={(e) => setEditFormData({ ...editFormData, rnc: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={editFormData.email || ''} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefono</Label><Input value={editFormData.phone || ''} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Sector</Label><Input value={editFormData.department || ''} onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })} /></div>
              <div className="space-y-2"><Label>Direccion</Label><Input value={editFormData.address || ''} onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Notas</Label><Textarea value={editFormData.notes || ''} onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} rows={2} /></div>
            <DialogFooter><Button variant="outline" onClick={() => setEditClientDialogOpen(false)}>Cancelar</Button><Button onClick={handleUpdateClient}>Guardar Cambios</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailClientDialogOpen} onOpenChange={setDetailClientDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedClient && (() => {
            const c = selectedClient;
            const cContacts = contactsList.filter(ct => ct.client_id === c.client_id);
            const cOpps = opportunitiesList.filter(o => o.client_id === c.client_id);
            const cCalls = callsList.filter(cl => cl.client_id === c.client_id);
            const activePipeline = cOpps.filter(o => !['won','lost'].includes(o.status)).reduce((s,o) => s + (o.value || 0), 0);
            const wonValue = cOpps.filter(o => o.status === 'won').reduce((s,o) => s + (o.value || 0), 0);
            return (<>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="font-heading text-xl flex items-center gap-2"><Building className="h-5 w-5" />{c.company || c.name}</DialogTitle>
                  <div className="flex gap-2">
                    <Button variant="default" size="sm" onClick={() => { setDetailClientDialogOpen(false); navigate(`/tasks?new=1&client_id=${c.client_id}`); }}><CheckSquare className="h-4 w-4 mr-2" />Agregar Tarea</Button>
                    <Button variant="outline" size="sm" onClick={() => { setDetailClientDialogOpen(false); handleOpenEditClient(c); }}><Edit className="h-4 w-4 mr-2" />Editar</Button>
                  </div>
                </div>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1"><p className="text-xs text-muted-foreground">Tipo Fiscal</p><p className="text-sm font-medium">{CLIENT_TYPES[c.client_type]?.label || c.client_type}</p></div>
                {c.rnc && <div className="space-y-1"><p className="text-xs text-muted-foreground">RNC</p><p className="text-sm font-mono">{c.rnc}</p></div>}
                {c.email && <div className="space-y-1"><p className="text-xs text-muted-foreground">Email</p><p className="text-sm">{c.email}</p></div>}
                {c.phone && <div className="space-y-1"><p className="text-xs text-muted-foreground">Telefono</p><p className="text-sm">{c.phone}</p></div>}
                {c.department && <div className="space-y-1"><p className="text-xs text-muted-foreground">Sector</p><p className="text-sm">{c.department}</p></div>}
                {c.address && <div className="space-y-1"><p className="text-xs text-muted-foreground">Direccion</p><p className="text-sm">{c.address}</p></div>}
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Facturado</p><p className="text-lg font-bold font-mono">{formatCurrency(c.total_invoiced)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Cobrado</p><p className="text-lg font-bold font-mono text-emerald-500">{formatCurrency(c.total_paid)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Pipeline</p><p className="text-lg font-bold font-mono text-blue-500">{formatCurrency(activePipeline)}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Ganadas</p><p className="text-lg font-bold font-mono text-emerald-600">{formatCurrency(wonValue)}</p></CardContent></Card>
              </div>
              {cOpps.length > 0 && (<div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Target className="h-4 w-4" />Pipeline ({cOpps.length})</h4>
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {Object.entries(OPPORTUNITY_STATUS).map(([key, { label: l, color: col }]) => {
                    const count = cOpps.filter(o => o.status === key).length;
                    if (count === 0) return null;
                    return <Badge key={key} variant="outline" className="text-xs gap-1"><div className={`w-1.5 h-1.5 rounded-full ${col}`} />{count} {l}</Badge>;
                  })}
                </div>
                <div className="space-y-2">{cOpps.map(o => (
                  <div key={o.opportunity_id} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                    <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${OPPORTUNITY_STATUS[o.status]?.color}`} /><span className="font-medium">{o.name}</span></div>
                    <div className="flex items-center gap-3"><span className={`text-xs ${VERTICALS[o.vertical]?.color || ''}`}>{VERTICALS[o.vertical]?.label || ''}</span><span className="font-mono font-bold">{formatCurrency(o.value)}</span></div>
                  </div>
                ))}</div>
              </div>)}
              {cContacts.length > 0 && (<div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><UserPlus className="h-4 w-4" />Contactos ({cContacts.length})</h4>
                <div className="space-y-2">{cContacts.map(ct => (
                  <div key={ct.contact_id} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                    <div><span className="font-medium">{ct.name}</span>{ct.position && <span className="text-muted-foreground ml-2">- {ct.position}</span>}</div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {ct.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{ct.email}</span>}
                      {ct.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{ct.phone}</span>}
                    </div>
                  </div>
                ))}</div>
              </div>)}
              {cCalls.length > 0 && (<div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Phone className="h-4 w-4" />Llamadas ({cCalls.length})</h4>
                <div className="space-y-2">{cCalls.map(cl => (
                  <div key={cl.call_id} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      {cl.call_type === 'outbound' ? <PhoneOutgoing className="h-3 w-3 text-blue-500" /> : <PhoneIncoming className="h-3 w-3 text-emerald-500" />}
                      <span>{cl.call_type === 'outbound' ? 'Saliente' : 'Entrante'} - {cl.duration_minutes} min</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(cl.created_at)}</span>
                  </div>
                ))}</div>
              </div>)}
              {cOpps.length === 0 && cContacts.length === 0 && cCalls.length === 0 && (
                <div className="text-center py-6 text-muted-foreground"><p className="text-sm">No hay actividad registrada para esta empresa.</p></div>
              )}
            </>);
          })()}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} itemType={deleteItem.type} itemId={deleteItem.id} itemName={deleteItem.name} isAdmin={isAdmin} onDeleted={loadData} directDelete={handleDeleteClient} />
    </div>
  );
}
